import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Swords, Anchor, Plane, ChevronRight, Clock, ArrowLeft, BookOpen } from "lucide-react";
import articlesApi from "../api/articles";

/* ─── data ──────────────────────────────────────────────────────── */
const BRANCH_DATA = {
  army: {
    label: "Indian Army", short: "ARMY", tagline: "Service Before Self",
    color: "#3d6b12", dark: "#1a2e05", mid: "#2d4a0e",
    accent: "#a3e635", muted: "rgba(163,230,53,0.12)", Icon: Swords,
    stats: [
      { label: "Active Personnel", value: "~14 Lakh" },
      { label: "Reserve", value: "~11 Lakh" },
      { label: "Commands", value: "7" },
      { label: "Founded", value: "1895" },
    ],
    commands: [
      { name: "Northern Command",      hq: "Udhampur",       area: "J&K · Ladakh · Himachal Pradesh · Punjab" },
      { name: "Western Command",       hq: "Chandimandir",   area: "Punjab · Haryana · Delhi · Rajasthan (part)" },
      { name: "South Western Command", hq: "Jaipur",         area: "Rajasthan (main) · Gujarat" },
      { name: "Southern Command",      hq: "Pune",           area: "Maharashtra · Goa · MP · Andhra Pradesh" },
      { name: "Eastern Command",       hq: "Kolkata",        area: "West Bengal · Sikkim · Arunachal · Northeast" },
      { name: "Central Command",       hq: "Lucknow",        area: "UP · Uttarakhand · Bihar · Jharkhand" },
      { name: "Army Training Command", hq: "Shimla",         area: "Training & Doctrine (ARTRAC)" },
    ],
    rankGroups: [
      { label: "Officers", color: "#3d6b12", light: "#f0fdf4",
        ranks: [
          { name: "General",            abbr: "Gen",     note: "COAS" },
          { name: "Lieutenant General", abbr: "Lt Gen",  note: "Army / Corps Commander" },
          { name: "Major General",      abbr: "Maj Gen", note: "Division Commander" },
          { name: "Brigadier",          abbr: "Brig",    note: "Brigade Commander" },
          { name: "Colonel",            abbr: "Col",     note: "Battalion Commander" },
          { name: "Lieutenant Colonel", abbr: "Lt Col",  note: "2IC / Battalion" },
          { name: "Major",              abbr: "Maj",     note: "Company Commander" },
          { name: "Captain",            abbr: "Capt",    note: "Platoon Commander" },
          { name: "Lieutenant",         abbr: "Lt",      note: "" },
          { name: "Second Lieutenant",  abbr: "2/Lt",    note: "Entry — commissioned" },
        ],
      },
      { label: "JCOs", color: "#92400e", light: "#fef3c7",
        ranks: [
          { name: "Subedar Major", abbr: "Sub Maj", note: "Senior JCO; regimental advisor" },
          { name: "Subedar",       abbr: "Sub",     note: "" },
          { name: "Naib Subedar", abbr: "Nb Sub",  note: "Entry JCO" },
        ],
      },
      { label: "Other Ranks", color: "#1e3a8a", light: "#eff6ff",
        ranks: [
          { name: "Havildar",         abbr: "Hav",  note: "" },
          { name: "Naik",             abbr: "Nk",   note: "" },
          { name: "Lance Naik",       abbr: "L/Nk", note: "" },
          { name: "Sepoy / Rifleman", abbr: "Sep",  note: "Entry soldier" },
        ],
      },
    ],
    facts: [
      ["HQ", "Integrated Defence HQ (IHQ-MoD), New Delhi"],
      ["Law", "Army Act, 1950"],
      ["Size", "World's 2nd largest standing army"],
      ["Special Forces", "Para SF — 1, 2, 4, 9, 10, 21 Para (SF) battalions"],
      ["Key Operations", "Op Vijay (Kargil 1999) · Op Rakshak (J&K) · Op Meghdoot (Siachen)"],
      ["UN Peacekeeping", "One of the largest troop contributors globally"],
    ],
  },

  navy: {
    label: "Indian Navy", short: "NAVY", tagline: "Sham No Varunah",
    color: "#1e3a8a", dark: "#0c1a3d", mid: "#162d6e",
    accent: "#93c5fd", muted: "rgba(147,197,253,0.12)", Icon: Anchor,
    stats: [
      { label: "Active Personnel", value: "~67,000" },
      { label: "Ships & Submarines", value: "~130+" },
      { label: "Commands", value: "4" },
      { label: "Founded", value: "1612" },
    ],
    commands: [
      { name: "Western Naval Command",     hq: "Mumbai",        area: "Arabian Sea · Persian Gulf · Red Sea · West coast" },
      { name: "Eastern Naval Command",     hq: "Visakhapatnam", area: "Bay of Bengal · Andaman Sea · East coast" },
      { name: "Southern Naval Command",    hq: "Kochi",         area: "Training Command · Lakshadweep · South coast" },
      { name: "Andaman & Nicobar Command", hq: "Port Blair",    area: "India's only tri-service theatre command" },
    ],
    rankGroups: [
      { label: "Officers", color: "#1e3a8a", light: "#eff6ff",
        ranks: [
          { name: "Admiral",              abbr: "Adm",    note: "CNS" },
          { name: "Vice Admiral",         abbr: "VAdm",   note: "Fleet / Command Commander" },
          { name: "Rear Admiral",         abbr: "RAdm",   note: "" },
          { name: "Commodore",            abbr: "Cdre",   note: "Flotilla / Base Commander" },
          { name: "Captain",              abbr: "Capt",   note: "Major warship Commander" },
          { name: "Commander",            abbr: "Cdr",    note: "Frigate / Corvette Commander" },
          { name: "Lieutenant Commander", abbr: "Lt Cdr", note: "" },
          { name: "Lieutenant",           abbr: "Lt",     note: "" },
          { name: "Sub Lieutenant",       abbr: "Sub Lt", note: "Entry — commissioned" },
        ],
      },
      { label: "Sailors", color: "#075985", light: "#f0f9ff",
        ranks: [
          { name: "Master Chief PO I",   abbr: "MCPO-I",  note: "" },
          { name: "Master Chief PO II",  abbr: "MCPO-II", note: "" },
          { name: "Chief Petty Officer", abbr: "CPO",     note: "" },
          { name: "Petty Officer",       abbr: "PO",      note: "" },
          { name: "Leading Seaman",      abbr: "LS",      note: "" },
          { name: "Seaman I / II",       abbr: "Sea",     note: "Entry sailor" },
        ],
      },
    ],
    facts: [
      ["HQ", "Integrated Defence HQ (IHQ-MoD), New Delhi"],
      ["Coastline", "7,516 km + 2.37 million sq km EEZ"],
      ["Aircraft Carrier", "INS Vikrant — India's first indigenous carrier (2022)"],
      ["SSBN", "INS Arihant — nuclear ballistic missile submarine"],
      ["Special Forces", "MARCOS — Marine Commandos"],
      ["Major Exercises", "Malabar (QUAD) · Milan · TROPEX · INDRA (India–Russia)"],
    ],
  },

  airforce: {
    label: "Indian Air Force", short: "IAF", tagline: "Nabham Sprusham Deeptam",
    color: "#075985", dark: "#062035", mid: "#0a3554",
    accent: "#7dd3fc", muted: "rgba(125,211,252,0.12)", Icon: Plane,
    stats: [
      { label: "Active Personnel", value: "~1.7 Lakh" },
      { label: "Aircraft Fleet", value: "~1,700+" },
      { label: "Commands", value: "7" },
      { label: "Founded", value: "1932" },
    ],
    commands: [
      { name: "Western Air Command",       hq: "New Delhi",          area: "J&K · Punjab · Haryana · Delhi · Rajasthan" },
      { name: "Eastern Air Command",       hq: "Shillong",           area: "Northeast India · Sikkim · West Bengal" },
      { name: "Southern Air Command",      hq: "Thiruvananthapuram", area: "Tamil Nadu · Kerala · Karnataka · A&N Islands" },
      { name: "South Western Air Command", hq: "Gandhinagar",        area: "Gujarat · Maharashtra · Goa · Madhya Pradesh" },
      { name: "Central Air Command",       hq: "Prayagraj",          area: "UP · Uttarakhand · Bihar · Jharkhand · Odisha" },
      { name: "Training Command",          hq: "Bengaluru",          area: "All IAF training establishments" },
      { name: "Maintenance Command",       hq: "Nagpur",             area: "Aircraft maintenance, repair & overhaul (MRO)" },
    ],
    rankGroups: [
      { label: "Officers", color: "#075985", light: "#f0f9ff",
        ranks: [
          { name: "Air Chief Marshal", abbr: "ACM",      note: "CAS" },
          { name: "Air Marshal",       abbr: "AM",        note: "AOC-in-C" },
          { name: "Air Vice Marshal",  abbr: "AVM",       note: "" },
          { name: "Air Commodore",     abbr: "Air Cdre",  note: "Station / Wing Commander" },
          { name: "Group Captain",     abbr: "Gp Capt",   note: "AF Station Commander" },
          { name: "Wing Commander",    abbr: "Wg Cdr",    note: "Squadron Commander" },
          { name: "Squadron Leader",   abbr: "Sqn Ldr",   note: "" },
          { name: "Flight Lieutenant", abbr: "Flt Lt",    note: "" },
          { name: "Flying Officer",    abbr: "Fg Offr",   note: "Entry — commissioned" },
        ],
      },
      { label: "Airmen", color: "#1d4ed8", light: "#eff6ff",
        ranks: [
          { name: "Master Warrant Officer", abbr: "MWO", note: "" },
          { name: "Warrant Officer",        abbr: "WO",  note: "" },
          { name: "Junior Warrant Officer", abbr: "JWO", note: "" },
          { name: "Sergeant",               abbr: "Sgt", note: "" },
          { name: "Corporal",               abbr: "Cpl", note: "" },
          { name: "Leading Aircraftman",    abbr: "LAC", note: "" },
          { name: "Aircraftman",            abbr: "AC",  note: "Entry airman" },
        ],
      },
    ],
    facts: [
      ["Air Force Day", "8 October every year"],
      ["Motto", "Nabham Sprusham Deeptam (Bhagavad Gita, Chapter 11, verse 24)"],
      ["Key Aircraft", "Rafale · Su-30MKI · Tejas LCA · C-17 Globemaster · C-130J Super Hercules"],
      ["Indigenous", "HAL Tejas — India's first indigenous Light Combat Aircraft"],
      ["Special Forces", "GARUD Commando Force"],
      ["Major Exercises", "Garuda (India–France) · Cope India (India–US) · Tasman Saber"],
    ],
  },
};

/* ─── main ──────────────────────────────────────────────────────── */
export default function BranchPage() {
  const { branch } = useParams();
  const navigate   = useNavigate();
  const [articles,  setArticles]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState("overview");
  const [rankGroup, setRankGroup] = useState(0);

  const d = BRANCH_DATA[branch];

  useEffect(() => {
    if (!d) return;
    setLoading(true); setTab("overview"); setRankGroup(0);
    articlesApi.list({ limit: 30 })
      .then(res => setArticles((res.items || res.data || res || []).filter(a => a.tags?.includes(branch))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [branch]);

  if (!d) return (
    <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">Branch not found</div>
  );

  const { label, short, tagline, color, dark, mid, accent, muted, Icon, stats, commands, rankGroups, facts } = d;
  const rg = rankGroups[rankGroup];

  return (
    <div className="min-h-screen" style={{ background: "#F1F3F6" }}>

      {/* ── HERO ── full bleed */}
      <div style={{ background: `linear-gradient(135deg, ${dark} 0%, ${mid} 60%, ${color} 100%)` }}>
        <div className="max-w-6xl mx-auto px-8">

          {/* top nav */}
          <div className="pt-6 pb-2 flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-[13px] font-semibold transition-opacity opacity-60 hover:opacity-100"
              style={{ color: accent }}
            >
              <ArrowLeft size={16} strokeWidth={2.5} /> Back
            </button>
            <span className="text-[10px] font-black tracking-[3px] px-3 py-1.5 rounded-full" style={{ background: muted, color: accent }}>
              {short}
            </span>
          </div>

          {/* hero content: left = identity, right = stats */}
          <div className="flex items-end justify-between pb-0 pt-4 gap-8">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: muted, border: `1.5px solid ${accent}30` }}>
                <Icon size={30} color={accent} strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-4xl font-black text-white tracking-tight leading-none">{label}</h1>
                <p className="text-[13px] mt-2 italic font-medium" style={{ color: accent, opacity: 0.65 }}>"{tagline}"</p>
              </div>
            </div>

            {/* stat chips — desktop right-aligned */}
            <div className="hidden md:flex items-center gap-3 pb-1">
              {stats.map(s => (
                <div key={s.label} className="text-center px-4 py-3 rounded-2xl" style={{ background: muted, border: `1px solid ${accent}20` }}>
                  <p className="text-[18px] font-black text-white leading-none">{s.value}</p>
                  <p className="text-[9px] uppercase tracking-widest mt-1 font-semibold" style={{ color: accent, opacity: 0.6 }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* mobile stats */}
          <div className="flex gap-2 mt-4 md:hidden">
            {stats.map(s => (
              <div key={s.label} className="flex-1 text-center px-2 py-2.5 rounded-xl" style={{ background: muted }}>
                <p className="text-[14px] font-black text-white">{s.value}</p>
                <p className="text-[8px] uppercase tracking-wider mt-0.5 font-semibold" style={{ color: accent, opacity: 0.6 }}>{s.label}</p>
              </div>
            ))}
          </div>

          {/* tab bar */}
          <div className="flex gap-1 mt-6">
            {[
              { key: "overview", label: "Overview" },
              { key: "ranks",    label: "Ranks" },
              { key: "articles", label: `Articles${articles.length ? ` (${articles.length})` : ""}` },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className="px-6 py-3 text-[13px] font-bold rounded-t-xl relative transition-all"
                style={tab === t.key
                  ? { background: "#F1F3F6", color }
                  : { background: "rgba(255,255,255,0.06)", color: `${accent}80` }
                }
              >
                {t.label}
                {tab === t.key && (
                  <motion.div layoutId="tab-line" className="absolute bottom-0 left-4 right-4 h-[3px] rounded-full" style={{ background: color }} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="max-w-6xl mx-auto px-8 py-8">
        <AnimatePresence mode="wait">

          {/* OVERVIEW — 2 column grid */}
          {tab === "overview" && (
            <motion.div key="ov" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* LEFT: commands + facts (2/3 width) */}
                <div className="lg:col-span-2 space-y-6">

                  {/* Commands */}
                  <div>
                    <SectionLabel text="Commands & Headquarters" color={color} />
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                      <table className="w-full">
                        <thead>
                          <tr style={{ background: `${color}08` }}>
                            <th className="text-left px-5 py-3 text-[10px] font-black uppercase tracking-widest" style={{ color: `${color}80` }}>#</th>
                            <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest" style={{ color: `${color}80` }}>Command</th>
                            <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest" style={{ color: `${color}80` }}>Area of Responsibility</th>
                            <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest" style={{ color: `${color}80` }}>HQ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {commands.map((c, i) => (
                            <tr key={c.name} style={{ background: i % 2 ? "#FAFBFC" : "white", borderTop: "1px solid #F3F4F6" }}>
                              <td className="px-5 py-3.5 text-[11px] font-black" style={{ color: `${color}50` }}>{String(i + 1).padStart(2, "0")}</td>
                              <td className="px-4 py-3.5">
                                <p className="text-[13px] font-bold text-gray-800">{c.name}</p>
                              </td>
                              <td className="px-4 py-3.5">
                                <p className="text-[12px] text-gray-500">{c.area}</p>
                              </td>
                              <td className="px-4 py-3.5">
                                <span className="inline-block text-[11px] font-bold px-3 py-1 rounded-lg whitespace-nowrap" style={{ background: muted, color }}>
                                  {c.hq}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Key Facts */}
                  <div>
                    <SectionLabel text="Key Facts" color={color} />
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                      {facts.map(([k, v], i) => (
                        <div key={k} className="flex items-start gap-4 px-5 py-4" style={{ background: i % 2 ? "#FAFBFC" : "white", borderTop: i > 0 ? "1px solid #F3F4F6" : "none" }}>
                          <span className="text-[11px] font-black whitespace-nowrap w-32 flex-shrink-0 mt-0.5" style={{ color }}>{k}</span>
                          <p className="text-[13px] text-gray-600 leading-relaxed">{v}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* RIGHT: articles (1/3 width) */}
                <div>
                  <SectionLabel text={`Latest Articles (${articles.length})`} color={color} />
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: color, borderTopColor: "transparent" }} />
                    </div>
                  ) : articles.length === 0 ? (
                    <div className="text-center py-12 text-gray-400 text-sm">No articles yet</div>
                  ) : (
                    <div className="space-y-3">
                      {articles.slice(0, 6).map(a => <ArticleCard key={a._id} article={a} color={color} muted={muted} navigate={navigate} />)}
                      {articles.length > 6 && (
                        <button onClick={() => setTab("articles")} className="w-full py-3 rounded-xl text-[12px] font-bold border-2 transition-all" style={{ borderColor: color, color }}>
                          View all {articles.length} articles →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* RANKS */}
          {tab === "ranks" && (
            <motion.div key="rk" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              {/* Group tabs */}
              <div className="flex gap-3 mb-6">
                {rankGroups.map((g, i) => (
                  <button key={g.label} onClick={() => setRankGroup(i)}
                    className="px-6 py-2.5 rounded-xl text-[13px] font-bold transition-all"
                    style={rankGroup === i ? { background: g.color, color: "#fff" } : { background: "white", color: g.color, border: `1.5px solid ${g.color}30` }}
                  >
                    {g.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
                {rg.ranks.map((r, i) => {
                  const isTop = i === 0;
                  return (
                    <motion.div
                      key={r.name}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="flex items-center gap-4 rounded-2xl px-5 py-4"
                      style={{
                        background: isTop ? rg.color : "white",
                        border: isTop ? "none" : `1.5px solid ${rg.color}20`,
                        boxShadow: isTop ? `0 4px 20px ${rg.color}40` : "0 1px 4px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-black text-[11px]"
                        style={{ background: isTop ? "rgba(255,255,255,0.2)" : rg.light, color: isTop ? "#fff" : rg.color }}
                      >
                        {r.abbr.length <= 5 ? r.abbr : r.abbr.split(" ")[0]}
                      </div>
                      <div>
                        <p className="text-[13px] font-bold leading-tight" style={{ color: isTop ? "#fff" : "#1a1a1a" }}>{r.name}</p>
                        {r.note && <p className="text-[11px] mt-0.5" style={{ color: isTop ? "rgba(255,255,255,0.65)" : "#9CA3AF" }}>{r.note}</p>}
                      </div>
                      <span className="ml-auto text-[10px] font-black" style={{ color: isTop ? "rgba(255,255,255,0.4)" : `${rg.color}40` }}>
                        #{rg.ranks.length - i}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-300 font-semibold mt-4 uppercase tracking-widest">↑ Highest seniority at top</p>
            </motion.div>
          )}

          {/* ARTICLES */}
          {tab === "articles" && (
            <motion.div key="ar" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              {loading ? (
                <div className="flex justify-center py-20">
                  <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: color, borderTopColor: "transparent" }} />
                </div>
              ) : articles.length === 0 ? (
                <div className="text-center py-20">
                  <BookOpen size={32} className="mx-auto text-gray-200 mb-3" />
                  <p className="text-gray-400 text-sm">No articles yet for this branch.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {articles.map(a => <ArticleCard key={a._id} article={a} color={color} muted={muted} navigate={navigate} />)}
                </div>
              )}
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}

function SectionLabel({ text, color }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="w-1 h-4 rounded-full" style={{ background: color }} />
      <p className="text-[11px] font-black uppercase tracking-[2px]" style={{ color }}>{text}</p>
    </div>
  );
}

function ArticleCard({ article, color, muted, navigate }) {
  return (
    <motion.div
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate(`/articles/${article.slug}`)}
      className="bg-white rounded-2xl p-4 cursor-pointer shadow-sm border border-gray-100 hover:shadow-md transition-all"
    >
      <p className="text-[13px] font-bold text-gray-800 leading-snug line-clamp-2 mb-2">{article.title}</p>
      <p className="text-[11px] text-gray-400 line-clamp-2 mb-3">{article.summary}</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 font-medium">{article.dateStr}</span>
          {article.readTime && (
            <span className="flex items-center gap-1 text-[10px] text-gray-400">
              <Clock size={9} /> {article.readTime} min
            </span>
          )}
        </div>
        <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: muted }}>
          <ChevronRight size={12} style={{ color }} />
        </div>
      </div>
    </motion.div>
  );
}
